import React, { useState, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useMst } from '../../model/Root';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import Table from '../../components/Table/Table';
import AddButton from '../../components/Button/AddButton';
import { useTranslation } from 'react-i18next';
import { DIGIMAGAZINE_TABLE } from '../../utils/constants';
import { Community, LinkRow, Row } from '../../types';
import AlertModal from '../../components/AlertModal';
import { Grid, FormControl, InputLabel, Select, MenuItem } from '@mui/material';

const DigiMagazineScreen = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    userStore: { communities, user, getCommunities },
    churchStore: { churches, fetchChurches },
    digiMagazineStore: { digiMagazines, getAllDigiMagazines, deleteDigiMagazine },
  } = useMst();
  const [selectedRow, setSelectedRow] = useState<Row | LinkRow>();
  const [result, setResult] = useState<Row[]>([]);
  const [churchFilter, setChurchFilter] = useState<string | 'all'>('all');
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const filterDigiMagazines = useCallback(
    (communities: Community[], ownCommunities: Community[]) => {
      const filtered = communities.filter((community: Community) => {
        return ownCommunities.some((el) => {
          return el.id === community.id;
        });
      });
      const filteredDigiMagazines = digiMagazines.filter((digiMagazine) => {
        return filtered.find(({ community }) => community === digiMagazine.publisher);
      });

      return filteredDigiMagazines;
    },
    [digiMagazines]
  );

  useEffect(() => {
    const getDigiMagazines = async () => {
      await getAllDigiMagazines();

      if (user?.admin) {
        const filteredByChurch = (digiMagazines ?? []).filter((m) =>
          churchFilter === 'all' ? true : (m as unknown as { churchId?: string | null }).churchId === churchFilter
        );
        const tempResult: Row[] =
          filteredByChurch.length > 0
            ? filteredByChurch.map((digiMagazine) => ({
                id: digiMagazine.id,
                name: digiMagazine.name,
                value: `${digiMagazine.name} - ${digiMagazine.publisher}`,
                secondValue: digiMagazine.published ? t('published') : t('draft'),
              }))
            : [];
        setResult(tempResult);
      } else {
        if (communities && user?.communities.length) {
          const filtered = filterDigiMagazines(communities, user.communities);
          const tempResult: Row[] =
            filtered && filtered.length > 0
              ? filtered.map((digiMagazine) => ({
                  id: digiMagazine.id,
                  name: digiMagazine.name,
                  value: `${digiMagazine.name} - ${digiMagazine.author}`,
                  secondValue: digiMagazine.published ? t('published') : t('draft'),
                }))
              : [];
          setResult(tempResult);
        }
      }
    };

    getDigiMagazines();
  }, [getAllDigiMagazines, getCommunities, user, communities, digiMagazines, t, filterDigiMagazines, churchFilter]);

  useEffect(() => {
    if (churches.length === 0) fetchChurches('', true);
  }, [fetchChurches, churches.length]);

  useEffect(() => {
    if (!selectedRow?.id || modalOpen) return;
    navigate(`/digiMagazines/${selectedRow.id}`);
  }, [selectedRow?.id, modalOpen, navigate]);

  const navigateTo = () => {
    navigate('/digiMagazines/create');
  };

  const deleteMagazine = async () => {
    if (selectedRow?.id) {
      await deleteDigiMagazine(selectedRow.id.toString());
      await getAllDigiMagazines();
      const tempResult: Row[] =
        digiMagazines.length > 0
          ? digiMagazines.map((digiMagazine) => ({
              id: digiMagazine.id,
              name: digiMagazine.name,
              value: `${digiMagazine.name} - ${digiMagazine.author}`,
              secondValue: digiMagazine.published ? t('published') : t('draft'),
            }))
          : [];
      setResult(tempResult);
      setSelectedRow(undefined);
    }
    setModalOpen(false);
  };

  const toggleDelete = (id: number) => {
    setModalOpen(true);
    setSelectedRow({ id: id });
  };

  const cancel = () => {
    setSelectedRow(undefined);
    setModalOpen(false);
  };

  return (
    <Layout title={t('digiMagazines.title')}>
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel id="church-filter">{t('church.title') ?? 'Church'}</InputLabel>
            <Select
              labelId="church-filter"
              label={t('church.title') ?? 'Church'}
              value={churchFilter}
              onChange={(e) => setChurchFilter(e.target.value as string)}
            >
              <MenuItem value={'all'}>{t('all') ?? 'All'}</MenuItem>
              <MenuItem value={'null'}>Ei seurakuntaa</MenuItem>
              {churches.map((c) => (
                <MenuItem key={c.id} value={c.id ?? ''}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <AddButton onClick={navigateTo} text={t('digiMagazines.add_btn')} />
      <Table
        tableType={DIGIMAGAZINE_TABLE}
        data={result}
        setSelectedRow={(value) => setSelectedRow({ id: value?.id, value: value?.value })}
        selectedRow={selectedRow}
        onDelete={(id) => toggleDelete(id)}
      />
      <AlertModal
        open={modalOpen}
        title="digiMagazines.delete"
        onClose={() => setModalOpen(!modalOpen)}
        leftButtonPress={cancel}
        rightButtonPress={deleteMagazine}
      />
    </Layout>
  );
});

export default DigiMagazineScreen;
